import { EditOutlined, LockOutlined } from '@ant-design/icons';
import { type DettaglioFirma } from '@framework/api-client/api/inquiries-compile/schemas/dettaglioFirma.ts';
import { ISODate } from '@framework/ui/components/date/ISODate.tsx';
import { getNameInitials } from '@framework/ui/utils/getNameInitials.ts';
import { Alert, Avatar, Flex, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CertificateValidityTag } from './CertificateValidityTag.tsx';
import { extractCertificateAuthority } from './extractCertificateAuthority.ts';
import { parseSignatureFormat, SignatureFormat } from './SignatureFormat.ts';
import { SignatureFormatTag } from './SignatureFormatTag.tsx';

const { Text } = Typography;

const BaseText = styled(Text)`
  line-height: 1.33;
`;

const SignatureTitle = styled(Flex)`
  margin-left: 2px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${props => props.theme.colorPrimary};
`;

const FieldLabel = styled(BaseText)`
  font-size: 12px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${props => props.theme.colorTextSecondary};
`;

const FieldValue = styled(BaseText)`
  font-size: 14px;
`;
const FieldCode = styled(FieldValue)`
  font-size: 13px;
  font-family: ${props => props.theme.fontFamilyCode || 'monospace'};
`;
const SignatureName = styled(BaseText)`
  font-size: 16px;
  margin: 0;
`;

const FieldRow = styled(Flex)`
  padding: 6px 0;
  border-bottom: 1px solid ${props => props.theme.colorSplit};
  &:last-child {
    border-bottom: none;
  }
`;

const CertificateBox = styled.div`
  border: 1px solid ${props => props.theme.colorSplit};
  background-color: ${props => props.theme.Layout?.bodyBg ?? props.theme.colorBgLayout};
  border-radius: 4px;
  padding: 8px 16px;
  margin-top: 6px;
`;

const CertificateTitle = styled(BaseText)`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${props => props.theme.colorText};
`;

const SignatureAvatar = styled(Avatar)`
  background-color: ${props => props.theme.colorPrimary};
`;

export interface ISignatureBlockProps {
  signature: DettaglioFirma;
}

/**
 * Riepilogo dei dati di una singola firma: formato, firmatario, data firma
 * e dettagli del certificato (per firme digitali) o avviso (per firme autografe).
 */
export function SignatureBlock({ signature }: Readonly<ISignatureBlockProps>) {
  const { t } = useTranslation();
  const format = parseSignatureFormat(signature.formato);
  const isHandwritten = format === SignatureFormat.Handwritten;
  const hasCertificateValidity = Boolean(
    signature.validitaCertificatoDal && signature.validitaCertificatoAl,
  );

  return (
    <Flex vertical gap={12}>
      <Flex align="center" justify="space-between">
        <SignatureTitle align="center" gap={8}>
          {isHandwritten ? <EditOutlined /> : <LockOutlined />}
          <span>
            {isHandwritten
              ? t('signer.firma-autografa', 'Firma autografa')
              : t('signer.firma-digitale', 'Firma digitale')}
          </span>
        </SignatureTitle>
        <SignatureFormatTag format={format} />
      </Flex>

      <Flex justify="space-between" align="center" gap={12}>
        <Flex align="center" gap={12}>
          <SignatureAvatar size={40}>
            {getNameInitials(signature.nomeCompleto)}
          </SignatureAvatar>
          <Flex vertical>
            <SignatureName strong>
              {signature.nomeCompleto ??
                t('signer.firmatario-sconosciuto', 'Firmatario sconosciuto')}
            </SignatureName>
            {signature.codiceFiscale && (
              <FieldCode>{signature.codiceFiscale}</FieldCode>
            )}
          </Flex>
        </Flex>
        {signature.data && (
          <Flex vertical>
            <FieldLabel>{t('signer.data-firma', 'Data firma')}</FieldLabel>
            <FieldValue>
              <ISODate date={signature.data} format="DD/MM/YYYY HH:mm" />
            </FieldValue>
          </Flex>
        )}
      </Flex>

      {isHandwritten ? (
        <Alert
          type="warning"
          message={t(
            'signer.firma-autografa-info',
            'Firma autografa — non sono disponibili dati di certificato o marca temporale.',
          )}
        />
      ) : (
        <CertificateBox>
          <FieldRow justify="space-between" align="center">
            <CertificateTitle>
              {t('signer.certificato', 'Certificato')}
            </CertificateTitle>
            <CertificateValidityTag
              signatureDate={signature.data}
              validFrom={signature.validitaCertificatoDal}
              validUntil={signature.validitaCertificatoAl}
            />
          </FieldRow>
          {signature.enteCertificatore && (
            <FieldRow justify="space-between" align="center" gap={12}>
              <FieldLabel>{t('signer.ente-cert', 'Ente cert.')}</FieldLabel>
              <FieldValue>
                {extractCertificateAuthority(signature.enteCertificatore)}
              </FieldValue>
            </FieldRow>
          )}
          {signature.serialeCertificato && (
            <FieldRow justify="space-between" align="center" gap={12}>
              <FieldLabel>{t('signer.seriale', 'Seriale')}</FieldLabel>
              <FieldCode>{signature.serialeCertificato}</FieldCode>
            </FieldRow>
          )}
          {hasCertificateValidity && (
            <FieldRow justify="space-between" align="center" gap={12}>
              <FieldLabel>{t('signer.validita', 'Validità')}</FieldLabel>
              <FieldValue>
                <ISODate date={signature.validitaCertificatoDal} />
                {' → '}
                <ISODate date={signature.validitaCertificatoAl} />
              </FieldValue>
            </FieldRow>
          )}
        </CertificateBox>
      )}
    </Flex>
  );
}
